import { useEffect, useMemo, useState } from 'react';
import { MIN_SEATS, deckLabel, maxSeatsForPool } from '@edh/shared';
import { DeckThumb } from './DeckThumb';
import { Sheet } from './Sheet';
import { draftPool } from '../lib/live';
import { useStore } from '../lib/store';

/**
 * Which decks are actually on the table tonight.
 *
 * The one thing the app cannot know and the one thing the draft depends on. A
 * deck is registered once and lives in the league forever; whether its owner
 * brought it this evening is a fact about a car boot. So the host says, and the
 * pool is exactly what they tick.
 *
 * Prefilled from the last time somebody opened a draft **today**, because the
 * second game of a night is played out of the same box as the first and nobody
 * should tick fifteen boxes twice. Never skipped, though, even when the list
 * comes back full: a deck registered between games would otherwise be silently
 * missing from a pool the host believed was complete, which is a worse failure
 * than one extra screen.
 */
export function DraftPoolSheet({
  onClose,
  onOpen,
  busy,
}: {
  onClose: () => void;
  /** Hand back the pool and how many chairs it can seat. */
  onOpen: (deckIds: string[], seats: number) => void;
  busy: boolean;
}) {
  const { state } = useStore();
  const decks = useMemo(
    () =>
      [...state.decks]
        .filter((d) => !d.retired)
        .sort((a, b) => deckLabel(a).localeCompare(deckLabel(b))),
    [state.decks],
  );

  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let live = true;
    void draftPool()
      .then((remembered) => {
        if (!live) return;
        // Intersected with what still exists, so a deck retired since this
        // morning does not come back as a ticked box nobody can untick.
        const real = new Set(decks.map((d) => d.id));
        setChosen(new Set(remembered.deckIds.filter((id) => real.has(id))));
      })
      // An empty pool is the right fallback and the right starting point.
      .catch(() => undefined)
      .finally(() => live && setLoaded(true));
    return () => {
      live = false;
    };
    // Once, on open. Re-running as the deck list settles would throw away ticks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seats = maxSeatsForPool(chosen.size);
  const enough = seats >= MIN_SEATS;
  /*
   * What the pool buys, and what one more deck would buy.
   *
   * Two a player is the minimum and it is a bad minimum: at exactly that, the
   * deal is two each, so after everybody bans one there are exactly as many
   * decks left as players and the last person to pick is handed the last deck.
   * One more turns that back into a choice, which is the difference between a
   * draft and a deal — so the screen asks for it rather than letting the host
   * discover it at the table.
   *
   * Past that, more decks are simply more choice: the whole pool is dealt, so
   * every deck ticked here is one somebody can ban or take. Worth saying once,
   * not worth nagging about.
   */
  const comfortable = seats * 2 + 1;
  const advice = !enough
    ? `${MIN_SEATS * 2 - chosen.size} more to seat a game`
    : chosen.size < comfortable
      ? `one more and a full table's last pick is still a choice`
      : `all ${chosen.size} go on the table`;

  const toggle = (id: string) =>
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Sheet title="What is on the table?" onClose={onClose}>
      <p className="hint" style={{ marginTop: -4 }}>
        Tick every deck physically here tonight. Two decks a player, so the pool decides how
        many can sit down.
      </p>

      {!loaded ? (
        <div className="empty">
          <span className="spinner" />
        </div>
      ) : (
        <div className="pool-list">
          {decks.map((deck) => {
            const on = chosen.has(deck.id);
            return (
              <button
                key={deck.id}
                className={`pool-row${on ? ' on' : ''}`}
                onClick={() => toggle(deck.id)}
                aria-pressed={on}
              >
                <DeckThumb deck={deck} size={38} />
                <span className="pool-name">{deckLabel(deck)}</span>
                <span className="pool-tick" aria-hidden="true">
                  {on ? '✓' : ''}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/*
        The count, said as what it buys rather than as a number.
        "Six decks" is a fact; "seats three" is the answer to the question the
        host is actually asking, which is whether everyone here can play. The
        line under it is the one that stops a thin pool becoming a bad draft.
      */}
      <p className={`hint pool-count${enough ? '' : ' short'}`} style={{ marginTop: 10 }}>
        <span className="num">{chosen.size}</span> deck{chosen.size === 1 ? '' : 's'}
        {enough && (
          <>
            {' · seats '}
            <span className="num">{seats}</span>
          </>
        )}
      </p>
      <p className={`hint pool-advice${enough && chosen.size < comfortable ? ' urge' : ''}`}>
        {advice}
      </p>

      <div className="btn-row" style={{ marginTop: 4 }}>
        <button className="btn ghost" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn primary"
          disabled={!enough || busy}
          onClick={() => onOpen([...chosen], seats)}
        >
          {busy ? <span className="spinner" /> : 'Start draft'}
        </button>
      </div>
    </Sheet>
  );
}
